// import { AuthConnection } from 'api-models'
import { OwnershipType, RuleDTO, RuleExecutionsResponseDTO, RuleNetworkSuggestionsDTO } from 'api-models'
import { toQuery } from 'utils'
import { ApiClient } from '../api-client'

async function createCustomRule(props: {
	accountId?: string | null
	payload: {
		asset_id?: string | null
		ad_account_id?: string | null
		campaign_id?: string | null
		rule_data: any
	}
}): Promise<RuleDTO> {
	const { accountId, payload } = props

	const { data } = await ApiClient.client.post(
		`/accounts/${accountId}/rules`,
		payload
	)
	return data.data
}

async function updateCustomRule(props: {
	accountId?: string | null
	ruleId?: string | null
	payload: {
		rule_data: any
		/** Send these — `null` included — to tell the API this caller owns the rule's scope
		 *  and may have just changed it. Omit them and the rule keeps whatever ownership
		 *  and targeting it was created with. */
		ad_account_id?: string | null
		campaign_id?: string | null
	}
}): Promise<RuleDTO> {
	const { accountId, ruleId, payload } = props

	const { data } = await ApiClient.client.put(
		`/accounts/${accountId}/rules/${ruleId}`,
		payload
	)
	return data.data
}

async function listAllRules(props: {
	accountId?: string | null
	query: {
		ownership?: OwnershipType
		asset_id?: string | null
		ad_account_id?: string | null
		campaign_id?: string | null
		include_deleted?: boolean | string
		[key: string]: any
	}
}): Promise<RuleDTO[]> {
	const { accountId, query } = props
	const queryParams = { ...query }
	if (queryParams.include_deleted === true) {
		queryParams.include_deleted = 'true' as any
	}

	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/rules?${toQuery(queryParams)}`
	)
	return data.data
}

async function toggleRuleStatus(props: {
	accountId?: string
	id: string
	enabled: boolean
}): Promise<RuleDTO> {
	const { accountId, id, enabled } = props

	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/rules/${id}/toggle`,
		{ enabled }
	)
	return data.data
}

async function deleteAllRules(props: {
	accountId?: string
	query: {
		asset_id?: string | null
		ad_account_id?: string | null
		campaign_id?: string | null
	}
}): Promise<void> {
	const { accountId, query } = props

	await ApiClient.client.delete(
		`/accounts/${accountId}/rules?${toQuery(query)}`
	)
}

async function deleteRule(props: {
	accountId?: string
	ruleId?: string
}): Promise<void> {
	const { accountId, ruleId } = props

	await ApiClient.client.delete(`/accounts/${accountId}/rules/${ruleId}`)
}

async function adAccountInheritSettings(props: {
	accountId?: string
	ad_account_id?: string
	inherit: boolean
}): Promise<void> {
	const { accountId, ad_account_id, inherit } = props

	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/${ad_account_id}/rules-settings`,
		{ inherit }
	)
	return data.data
}

async function campaignInheritSettings(props: {
	accountId?: string
	ad_account_id?: string
	campaign_id?: string
	inherit: boolean
	platform: any
}): Promise<void> {
	const { accountId, ad_account_id, campaign_id, inherit, platform } = props

	const { data } = await ApiClient.client.patch(
		`/accounts/${accountId}/ad-accounts/${ad_account_id}/campaigns/${campaign_id}/rules-settings?platform=${platform}`,
		{ inherit }
	)
	return data.data
}

async function getRuleExecutions(props: {
	accountId?: string | null
	query: {
		asset_id: string
		interval?: number
		from?: number
		to?: number
		rule_ids?: string
		status?: string
		search?: string
		sort?: string
	}
}): Promise<RuleExecutionsResponseDTO> {
	const { accountId, query } = props

	const { data } = await ApiClient.client.get(
		`/accounts/${accountId}/rules/executions?${toQuery(query)}`
	)
	return data.data
}

async function getNetworkSuggestions(props: {
	accountId: string
	assetId: string
}): Promise<RuleNetworkSuggestionsDTO> {
	const { data } = await ApiClient.client.get(
		`/accounts/${props.accountId}/rules/network-suggestions?${toQuery({ asset_id: props.assetId })}`
	)
	return data.data
}

export const Rules = {
	createCustomRule,
	updateCustomRule,
	listAllRules,
	toggleRuleStatus,
	deleteAllRules,
	deleteRule,
	adAccountInheritSettings,
	campaignInheritSettings,
	getRuleExecutions,
	getNetworkSuggestions
}
